import { useEffect, useState } from 'react';
import { Box } from '../ui/primitives';

/**
 * Indeterminate progress bar pinned to the top of the viewport.
 *
 * Gives immediate "something is happening" feedback on slow queries — an
 * all-time range takes a couple of seconds, and skeletons alone can read as a
 * broken layout rather than as work in progress.
 *
 * Two deliberate behaviours:
 *
 *  - **Appears only after a short delay.** Most queries finish in well under
 *    150 ms; flashing a bar for every one of those is visual noise. The bar is
 *    for the slow cases it was added to cover.
 *  - **Stays up for a minimum once shown.** Without a floor, a query that
 *    resolves just past the delay produces a one-frame flicker, which reads as
 *    a glitch.
 */
const SHOW_AFTER_MS = 150;
const MIN_VISIBLE_MS = 400;

export function TopProgressBar({ active }: { active: boolean }): JSX.Element | null {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let showTimer: number | undefined;
    let hideTimer: number | undefined;

    if (active) {
      showTimer = window.setTimeout(() => setVisible(true), SHOW_AFTER_MS);
    } else if (visible) {
      hideTimer = window.setTimeout(() => setVisible(false), MIN_VISIBLE_MS);
    }

    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
    // `visible` is intentionally read but not tracked: adding it would restart
    // the hide timer on every toggle and defeat the minimum-visible floor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  if (!visible) return null;

  return (
    <Box
      role="progressbar"
      aria-label="Loading dashboard data"
      aria-busy
      className="fixed inset-x-0 top-0 z-50 h-[3px] overflow-hidden pointer-events-none"
      style={{ backgroundColor: 'color-mix(in srgb, var(--life-transit) 18%, transparent)' }}
    >
      <Box
        className="h-full w-2/5 animate-indeterminate"
        style={{
          background:
            'linear-gradient(90deg, transparent, var(--life-transit) 35%, var(--life-transit) 65%, transparent)',
        }}
      />
    </Box>
  );
}
